import { marked } from "marked";
import Swal from "sweetalert2";
import { ApiService } from "../shared/utils/ApiService.js";
import { AppConfig } from "../shared/utils/AppConfig.js";
import { AuthService } from "../shared/services/AuthService.js";
import { TemplateEngine } from "../shared/utils/TemplateEngine.js";
import { askWithStream, renderMetaRespuesta } from "../shared/utils/ChatStreamHelper.js";
import { ChatsService, historialParaApi, tituloDesde } from "../shared/utils/ChatsService.js";
import { VistaService, VISTAS } from "../shared/utils/VistaService.js";
import { ConsultasService } from "../shared/utils/ConsultasService.js";
import { HistorialService } from "../shared/utils/HistorialService.js";
import { getModeloIa } from "../shared/utils/ModeloIa.js";
import { escapeHtml, sanitizeHtml } from "../shared/utils/sanitize.js";
import { isImageFile, iconForFile, formatSize, previewKind, tipoForFile } from "../shared/utils/adjuntos.js";
import { ProfileController } from "../profile/ProfileController.js";

import chatTemplate from "./templates/chat.html?raw";

function getCategoriasPermitidas(user) {
  if (!user) return null;
  if (user.rol === "admin") return null;
  return Array.isArray(user.categorias) && user.categorias.length > 0 ? user.categorias : null;
}

// URLs de archivos: absolutas http(s) o del mismo origen; nada de javascript:
function srcUrl(value) {
  const url = String(value || "").trim();
  return /^https?:\/\//i.test(url) || url.startsWith("/") ? escapeHtml(url) : "";
}

function extractYtId(url) {
  if (!url) return null;
  if (/^[a-zA-Z0-9_-]{7,12}$/.test(url.trim())) return url.trim();
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split("?")[0];
    return u.searchParams.get("v");
  } catch { return null; }
}

function renderMarkdown(text) {
  return sanitizeHtml(marked.parse(text || "", { breaks: true }));
}

const LOADING_HTML = '<span class="kbc-thinking"><i class="fas fa-circle-notch fa-spin me-2"></i>Buscando en la base de conocimiento...</span>';

// Estado de la pantalla (una sola instancia a la vez)
const state = {
  userId: null,
  chat: null,
  categorias: [],
  catMap: {},
  busy: false,
  doc: null, // { art, adjuntoId }
};

export const ChatController = {

  render: async(params = new URLSearchParams()) => {
    const user = AuthService.getCurrentUser();
    state.userId = user?.id || user?.email || "anon";
    state.busy = false;
    state.doc = null;

    let categorias = [];
    let modeloIa = "";
    try {
      const [categoriasResp, modelo] = await Promise.all([ApiService.get("categorias"), getModeloIa()]);
      categorias = categoriasResp?.data || [];
      modeloIa = modelo;
    } catch (error) {
      console.error("Error loading chat:", error);
    }

    // Mismas categorías que ve en Knowledge, sin las de tipo api (no tienen artículos)
    const permitidas = getCategoriasPermitidas(user);
    state.categorias = categorias.filter(c =>
      (!permitidas || permitidas.includes(c.id) || !!c.es_publica) && c.tipo !== "api",
    );
    state.catMap = Object.fromEntries(state.categorias.map(c => [c.id, c]));

    const chatId = params.get("id");
    state.chat = (chatId && ChatsService.get(state.userId, chatId)) || ChatsService.create();
    if (state.chat.categoriaId && !state.catMap[state.chat.categoriaId]) state.chat.categoriaId = null;
    if (chatId && state.chat.id !== chatId) history.replaceState(null, "", "#chat");

    const nombre = user?.nombre || "Usuario";
    document.getElementById("app").innerHTML = TemplateEngine.render(chatTemplate, {
      appName: escapeHtml(AppConfig.app.name),
      userName: escapeHtml(nombre),
      userEmail: escapeHtml(user?.email || user?.usuario || ""),
      userInitial: escapeHtml(nombre.charAt(0).toUpperCase()),
      nombreCorto: escapeHtml(nombre.split(" ")[0]),
      puedeGestionar: AuthService.puedeGestionar(user),
      modeloIa: modeloIa ? `Powered by ${escapeHtml(modeloIa)}` : "",
    });

    ChatController._renderScope();
    ChatController._renderRecientes();
    ChatController._renderThread();
    ChatController._bind();
    document.getElementById("kbc-input")?.focus();
  },

  // ── Conversaciones ─────────────────────────────────────────────────────

  _abrirChat: (chat) => {
    state.chat = chat;
    if (chat.categoriaId && !state.catMap[chat.categoriaId]) chat.categoriaId = null;
    history.replaceState(null, "", chat.mensajes.length ? `#chat?id=${encodeURIComponent(chat.id)}` : "#chat");
    ChatController._cerrarDoc();
    ChatController._renderScope();
    ChatController._renderRecientes();
    ChatController._renderThread();
    ChatController._cerrarSidebar();
    document.getElementById("kbc-input")?.focus();
  },

  _nuevoChat: () => {
    if (state.busy) return;
    ChatController._abrirChat(ChatsService.create({ categoriaId: state.chat?.categoriaId || null }));
  },

  _renderRecientes: () => {
    const el = document.getElementById("kbc-recents");
    if (!el) return;
    const chats = ChatsService.list(state.userId);
    el.innerHTML = chats.length
      ? chats.map(c => `
        <div class="kbc-recent${c.id === state.chat?.id ? " active" : ""}">
          <button type="button" class="kbc-recent-open" data-chat-id="${escapeHtml(c.id)}" title="${escapeHtml(c.titulo)}">${escapeHtml(c.titulo)}</button>
          <button type="button" class="kbc-recent-del" data-del-id="${escapeHtml(c.id)}" title="Borrar conversación" aria-label="Borrar conversación"><i class="fas fa-trash-can"></i></button>
        </div>`).join("")
      : '<div class="kbc-recents-empty">Tus conversaciones aparecen acá.</div>';
  },

  _borrarChat: async(chatId) => {
    const chat = ChatsService.get(state.userId, chatId);
    if (!chat) return;
    const { isConfirmed } = await Swal.fire({
      title: "¿Borrar la conversación?",
      text: chat.titulo,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Borrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });
    if (!isConfirmed) return;
    ChatsService.remove(state.userId, chatId);
    if (state.chat?.id === chatId) ChatController._abrirChat(ChatsService.create());
    else ChatController._renderRecientes();
  },

  // ── Alcance (categoría) ────────────────────────────────────────────────

  _renderScope: () => {
    const actual = state.chat?.categoriaId || null;
    const label = document.getElementById("kbc-scope-label");
    if (label) label.textContent = actual ? state.catMap[actual]?.nombre : "Todas las categorías";
    document.getElementById("kbc-scope")?.classList.toggle("is-set", !!actual);

    const item = (id, nombre, icono) => {
      const activo = (id || null) === actual;
      return `<button type="button" class="kbc-cat${activo ? " active" : ""}" data-cat-id="${escapeHtml(id || "")}">
        <i class="fas ${escapeHtml(icono)} fa-fw"></i><span>${escapeHtml(nombre)}</span>${activo ? '<i class="fas fa-check ms-auto"></i>' : ""}
      </button>`;
    };
    const items = [
      item(null, "Todas las categorías", "fa-globe"),
      ...state.categorias.map(c => item(c.id, c.nombre, c.icono || "fa-folder")),
    ];
    const lista = document.getElementById("kbc-cats-list");
    if (lista) lista.innerHTML = items.join("");
    const menu = document.getElementById("kbc-scope-menu");
    if (menu) menu.innerHTML = items.map(i => `<li>${i.replace('class="kbc-cat', 'class="dropdown-item kbc-cat')}</li>`).join("");
  },

  _setScope: (categoriaId) => {
    state.chat.categoriaId = categoriaId || null;
    if (state.chat.mensajes.length) ChatsService.save(state.userId, state.chat);
    ChatController._renderScope();
  },

  // ── Mensajes ───────────────────────────────────────────────────────────

  _renderThread: () => {
    const thread = document.getElementById("kbc-thread");
    if (!thread) return;
    thread.innerHTML = "";
    for (const m of state.chat.mensajes) thread.appendChild(ChatController._mensajeEl(m));
    ChatController._setEmpty(!state.chat.mensajes.length);
    const titulo = document.getElementById("kbc-topbar-title");
    if (titulo) titulo.textContent = state.chat.mensajes.length ? state.chat.titulo : AppConfig.app.name;
    ChatController._scrollAbajo();
  },

  _mensajeEl: (m) => {
    const wrap = document.createElement("div");
    wrap.className = `kbc-msg ${m.role === "user" ? "user" : "assistant"}`;
    const bubble = document.createElement("div");
    bubble.className = `kb-chat-bubble ${m.role === "user" ? "user" : "assistant"}`;
    wrap.appendChild(bubble);
    if (m.role === "user") {
      bubble.textContent = m.content;
    } else {
      bubble.innerHTML = renderMarkdown(m.content);
      renderMetaRespuesta(bubble, m);
    }
    return wrap;
  },

  _setEmpty: (empty) => {
    document.getElementById("kbc-app")?.classList.toggle("is-empty", empty);
  },

  _scrollAbajo: () => {
    const scroll = document.getElementById("kbc-scroll");
    if (scroll) scroll.scrollTop = scroll.scrollHeight;
  },

  _setBusy: (busy) => {
    state.busy = busy;
    const input = document.getElementById("kbc-input");
    if (input) input.disabled = busy;
    ChatController._updateSend();
  },

  _updateSend: () => {
    const input = document.getElementById("kbc-input");
    const send = document.getElementById("kbc-send");
    if (send) send.disabled = state.busy || !input?.value.trim();
  },

  _preguntar: async(question) => {
    if (state.busy || !question) return;
    // La respuesta se guarda en esta conversación aunque el usuario cambie a otra mientras llega
    const chat = state.chat;
    const historial = historialParaApi(chat);

    chat.mensajes.push({ role: "user", content: question });
    if (chat.mensajes.length === 1) chat.titulo = tituloDesde(question);

    const thread = document.getElementById("kbc-thread");
    const scroll = document.getElementById("kbc-scroll");
    thread.appendChild(ChatController._mensajeEl({ role: "user", content: question }));
    const wrap = document.createElement("div");
    wrap.className = "kbc-msg assistant";
    const bubble = document.createElement("div");
    bubble.className = "kb-chat-bubble assistant";
    bubble.innerHTML = LOADING_HTML;
    wrap.appendChild(bubble);
    thread.appendChild(wrap);
    ChatController._setEmpty(false);
    const titulo = document.getElementById("kbc-topbar-title");
    if (titulo) titulo.textContent = chat.titulo;
    ChatController._scrollAbajo();
    ChatController._setBusy(true);

    try {
      const { fuentes, fuentesDetalle, correccion, sugerencias, text } = await askWithStream({
        question,
        bubble,
        msgs: scroll,
        iconHtml: "",
        history: historial,
        categoriaId: chat.categoriaId,
      });
      chat.mensajes.push({ role: "assistant", content: text, fuentesDetalle, correccion, sugerencias });
      if (fuentes.length) {
        ConsultasService.add(question);
      } else {
        ChatController._ofrecerSugerencia(wrap, question);
      }
    } catch {
      bubble.innerHTML = '<span class="text-danger"><i class="fas fa-triangle-exclamation me-2"></i>No se pudo consultar la IA. Probá de nuevo en un momento.</span>';
    } finally {
      ChatsService.save(state.userId, chat);
      if (state.chat === chat) history.replaceState(null, "", `#chat?id=${encodeURIComponent(chat.id)}`);
      ChatController._renderRecientes();
      ChatController._setBusy(false);
      ChatController._scrollAbajo();
      document.getElementById("kbc-input")?.focus();
    }
  },

  // Sin fuentes: ofrece mandar la pregunta como sugerencia de tema (igual que el dashboard)
  _ofrecerSugerencia: (wrap, question) => {
    const el = document.createElement("div");
    el.className = "kbc-suggest";
    el.innerHTML = '<button type="button" class="btn btn-link btn-sm p-0"><i class="fas fa-lightbulb me-1 text-warning"></i>¿Sugerir este tema?</button>';
    el.querySelector("button").addEventListener("click", async() => {
      el.innerHTML = '<small class="text-muted"><i class="fas fa-check me-1 text-success"></i>Gracias, recibimos tu consulta.</small>';
      try { await ApiService.post("sugerencias", { texto: question, tipo: "ai" }); } catch {}
    });
    wrap.appendChild(el);
  },

  // ── Panel del documento ────────────────────────────────────────────────

  _abrirFuente: async(articuloId, adjuntoId = null) => {
    const panel = document.getElementById("kbc-doc");
    const body = document.getElementById("kbc-doc-body");
    if (!panel || !body) return;
    panel.hidden = false;
    document.getElementById("kbc-app")?.classList.add("doc-open");

    if (state.doc?.art?.id !== articuloId) {
      // "Abrir artículo completo" recién se muestra con el artículo cargado
      document.getElementById("kbc-doc-full").classList.add("invisible");
      document.getElementById("kbc-doc-titulo").textContent = "Cargando...";
      document.getElementById("kbc-doc-cat").textContent = "";
      document.getElementById("kbc-doc-files").innerHTML = "";
      body.innerHTML = '<div class="text-center text-muted py-5"><i class="fas fa-circle-notch fa-spin"></i></div>';
      try {
        const resp = await ApiService.post("kb/articulo", { id: articuloId });
        if (!resp?.data) throw new Error(resp?.error || "Artículo no encontrado");
        state.doc = { art: resp.data, adjuntoId: null };
        HistorialService.add(resp.data);
      } catch (err) {
        state.doc = null;
        document.getElementById("kbc-doc-titulo").textContent = "No se pudo abrir";
        body.innerHTML = `<div class="alert alert-warning m-3">${escapeHtml(err.message || "Error al cargar el artículo")}</div>`;
        return;
      }
    }

    const { art } = state.doc;
    const principal = (art.adjuntos || []).find(a => a.esPrincipal);
    const adicionales = (art.adjuntos || []).filter(a => !a.esPrincipal);
    // Un adjunto citado que es el principal se muestra como el artículo
    const adjunto = adjuntoId && adjuntoId !== principal?.id ? adicionales.find(a => a.id === adjuntoId) : null;
    state.doc.adjuntoId = adjunto?.id || null;

    document.getElementById("kbc-doc-titulo").textContent = art.titulo || "Artículo";
    document.getElementById("kbc-doc-cat").textContent = state.catMap[art.categoriaId]?.nombre || "";
    const full = document.getElementById("kbc-doc-full");
    full.setAttribute("href", `#article?id=${encodeURIComponent(art.id)}`);
    full.classList.remove("invisible");

    document.getElementById("kbc-doc-files").innerHTML = adicionales.length
      ? [
        `<button type="button" class="kbc-doc-chip${adjunto ? "" : " active"}" data-doc-adj=""><i class="fas fa-file-lines"></i>Artículo</button>`,
        ...adicionales.map(a => `<button type="button" class="kbc-doc-chip${adjunto?.id === a.id ? " active" : ""}" data-doc-adj="${escapeHtml(a.id)}" title="${escapeHtml(a.nombre)}"><i class="fas ${iconForFile(a.nombre)}"></i>${escapeHtml(a.nombre)}</button>`),
        ].join("")
      : "";

    body.innerHTML = adjunto ? ChatController._adjuntoHtml(adjunto) : ChatController._articuloHtml(art);
    body.scrollTop = 0;
    if (adjunto) ChatController._cargarTexto(adjunto);
  },

  _articuloHtml: (art) => {
    const url = srcUrl(art.urlRecurso);
    const partes = [];
    if (art.descripcion) partes.push(`<p class="kbc-doc-desc">${escapeHtml(art.descripcion)}</p>`);

    if (art.tipo === "pdf" && url) {
      // Chrome de Android no muestra PDF embebidos: siempre queda el enlace para abrirlo
      partes.push(`<iframe src="${url}" class="kbc-doc-frame" title="${escapeHtml(art.titulo)}"></iframe>
        <div class="kbc-doc-file-foot"><small class="text-muted">PDF</small><a href="${url}" target="_blank" rel="noopener" class="btn btn-outline-secondary btn-sm"><i class="fas fa-up-right-from-square me-1"></i>Abrir PDF</a></div>`);
    } else if (art.tipo === "imagen" && url) {
      partes.push(`<img src="${url}" alt="${escapeHtml(art.titulo)}" class="kbc-doc-img">`);
    } else if (art.tipo === "video") {
      const yt = extractYtId(art.urlRecurso);
      if (yt) partes.push(`<div class="ratio ratio-16x9 mb-3"><iframe src="https://www.youtube.com/embed/${encodeURIComponent(yt)}" title="${escapeHtml(art.titulo)}" allowfullscreen></iframe></div>`);
      else if (url) partes.push(`<video controls class="w-100 rounded mb-3" src="${url}"></video>`);
    } else if (art.tipo === "audio" && url) {
      partes.push(`<audio controls class="w-100 mb-3" src="${url}"></audio>`);
    }

    if (art.contenido && art.tipo === "texto") {
      // Quill produce HTML (empieza con "<"); el mock usa markdown
      const html = art.contenido.trimStart().startsWith("<") ? sanitizeHtml(art.contenido) : renderMarkdown(art.contenido);
      partes.push(`<div class="kb-content-text kbc-doc-text">${html}</div>`);
    }
    if (art.transcripcion && (art.tipo === "audio" || art.tipo === "video")) {
      const parrafos = art.transcripcion.split("\n").filter(p => p.trim()).map(p => `<p>${escapeHtml(p)}</p>`).join("");
      partes.push(`<h6 class="text-muted mt-3"><i class="fas fa-file-lines me-1"></i>Transcripción</h6><div class="kb-content-text kbc-doc-text">${parrafos}</div>`);
    }
    return partes.join("") || '<p class="text-muted">Este artículo no tiene contenido para mostrar acá.</p>';
  },

  _adjuntoHtml: (adj) => {
    const url = srcUrl(adj.url);
    const nombre = escapeHtml(adj.nombre);
    const descarga = url
      ? `<a href="${url}" target="_blank" rel="noopener" download class="btn btn-outline-secondary btn-sm"><i class="fas fa-download me-1"></i>Descargar</a>`
      : "";
    const pie = `<div class="kbc-doc-file-foot"><small class="text-muted text-truncate">${nombre}${adj.tamano ? ` · ${formatSize(adj.tamano)}` : ""}</small>${descarga}</div>`;
    if (!url) return `<p class="text-muted">${nombre}</p>`;

    const kind = previewKind(adj.nombre);
    if (isImageFile(adj.nombre)) return `<img src="${url}" alt="${nombre}" class="kbc-doc-img">${pie}`;
    if (kind === "pdf") return `<iframe src="${url}" class="kbc-doc-frame" title="${nombre}"></iframe>${pie}`;
    if (kind === "md" || kind === "txt") {
      return `<div class="kbc-doc-text" id="kbc-doc-texto"><div class="text-center text-muted py-4"><i class="fas fa-circle-notch fa-spin"></i></div></div>${pie}`;
    }
    const tipo = tipoForFile(adj.nombre);
    if (tipo === "audio") return `<audio controls class="w-100 mb-3" src="${url}"></audio>${pie}`;
    if (tipo === "video") return `<video controls class="w-100 rounded mb-3" src="${url}"></video>${pie}`;
    return `<div class="kbc-doc-generic"><i class="fas ${iconForFile(adj.nombre)} fa-3x text-muted mb-3"></i><div class="fw-semibold mb-1">${nombre}</div><p class="text-muted small">Este tipo de archivo no se puede ver acá.</p>${descarga}</div>`;
  },

  // Markdown y texto plano se traen y se muestran en el panel
  _cargarTexto: async(adj) => {
    const kind = previewKind(adj.nombre);
    if (kind !== "md" && kind !== "txt") return;
    try {
      const res = await fetch(adj.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const el = document.getElementById("kbc-doc-texto");
      if (!el || state.doc?.adjuntoId !== adj.id) return;
      el.innerHTML = kind === "md"
        ? `<div class="kb-content-text">${renderMarkdown(text)}</div>`
        : `<pre class="mb-0">${escapeHtml(text)}</pre>`;
    } catch (err) {
      const el = document.getElementById("kbc-doc-texto");
      if (el) el.innerHTML = `<div class="alert alert-warning mb-0">No se pudo abrir el archivo (${escapeHtml(err.message)}).</div>`;
    }
  },

  _cerrarDoc: () => {
    const panel = document.getElementById("kbc-doc");
    if (panel) panel.hidden = true;
    document.getElementById("kbc-app")?.classList.remove("doc-open");
    const body = document.getElementById("kbc-doc-body");
    if (body) body.innerHTML = ""; // corta la reproducción de audio/video
    state.doc = null;
  },

  _cerrarSidebar: () => {
    const el = document.getElementById("kbcSidebar");
    if (el && window.bootstrap) bootstrap.Offcanvas.getInstance(el)?.hide();
  },

  // ── Eventos ────────────────────────────────────────────────────────────

  _bind: () => {
    const input = document.getElementById("kbc-input");
    const form = document.getElementById("kbc-form");

    const autosize = () => {
      input.style.height = "auto";
      input.style.height = `${Math.min(input.scrollHeight, 200)}px`;
    };
    input?.addEventListener("input", () => { autosize(); ChatController._updateSend(); });
    // Enter envía; Shift+Enter agrega una línea
    input?.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        form.requestSubmit();
      }
    });
    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      const question = input.value.trim();
      if (!question || state.busy) return;
      input.value = "";
      autosize();
      ChatController._preguntar(question);
    });

    document.getElementById("kbc-new")?.addEventListener("click", ChatController._nuevoChat);
    document.getElementById("kbc-new-mobile")?.addEventListener("click", ChatController._nuevoChat);
    document.getElementById("kbc-brand")?.addEventListener("click", (e) => {
      e.preventDefault();
      ChatController._nuevoChat();
    });

    document.getElementById("kbc-recents")?.addEventListener("click", (e) => {
      const del = e.target.closest("[data-del-id]");
      if (del) { ChatController._borrarChat(del.dataset.delId); return; }
      const open = e.target.closest("[data-chat-id]");
      if (!open || state.busy) return;
      const chat = ChatsService.get(state.userId, open.dataset.chatId);
      if (chat) ChatController._abrirChat(chat);
    });

    const onCat = (e) => {
      const btn = e.target.closest("[data-cat-id]");
      if (!btn) return;
      ChatController._setScope(btn.dataset.catId || null);
      if (btn.closest("#kbc-cats-list")) ChatController._cerrarSidebar();
      document.getElementById("kbc-input")?.focus();
    };
    document.getElementById("kbc-cats-list")?.addEventListener("click", onCat);
    document.getElementById("kbc-scope-menu")?.addEventListener("click", onCat);

    // Fuentes: se abren en el panel lateral en lugar de navegar al artículo
    document.getElementById("kbc-thread")?.addEventListener("click", (e) => {
      const link = e.target.closest(".kb-chat-source[data-articulo-id]");
      if (!link || e.ctrlKey || e.metaKey) return; // Ctrl+clic abre el artículo en otra pestaña
      e.preventDefault();
      ChatController._abrirFuente(link.dataset.articuloId, link.dataset.adjuntoId || null);
    });
    document.getElementById("kbc-doc-files")?.addEventListener("click", (e) => {
      const chip = e.target.closest("[data-doc-adj]");
      if (chip && state.doc) ChatController._abrirFuente(state.doc.art.id, chip.dataset.docAdj || null);
    });
    document.getElementById("kbc-doc-close")?.addEventListener("click", ChatController._cerrarDoc);
    document.getElementById("kbc-app")?.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && state.doc) ChatController._cerrarDoc();
    });

    document.getElementById("kbc-vista-clasica")?.addEventListener("click", () => VistaService.set(VISTAS.clasica));
    document.getElementById("kbc-profile")?.addEventListener("click", (e) => {
      e.preventDefault();
      ProfileController.open();
    });
    document.getElementById("kbc-logout")?.addEventListener("click", (e) => {
      e.preventDefault();
      AuthService.logout();
    });
  },
};
